import { BRAND_NAME } from "@/lib/brand";
import {
  asLandingTextValue,
  styledTextString,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingComparisonColumn = {
  id: string;
  label: LandingTextValue;
  highlight?: boolean;
};

export type LandingComparisonCellMode = "check" | "cross" | "text";

export type LandingComparisonCell = {
  mode: LandingComparisonCellMode;
  text: string;
  content?: LandingTextValue;
};

export type LandingComparisonRow = {
  feature?: LandingTextValue;
  cells?: Record<string, LandingComparisonCell | string>;
  ours?: string;
  other?: string;
  third?: string;
};

export type LandingComparisonNormalizedRow = {
  feature: LandingTextValue;
  cells: Record<string, LandingComparisonCell>;
};

export type LandingComparisonColumns = {
  featureLabel: LandingTextValue;
  oursLabel: LandingTextValue;
  otherLabel: LandingTextValue;
  thirdLabel: LandingTextValue;
};

export type LandingComparisonTable = {
  featureLabel: LandingTextValue;
  columns: LandingComparisonColumn[];
  rows: LandingComparisonNormalizedRow[];
};

export const MAX_COMPARISON_COLUMNS = 6;
export const MIN_COMPARISON_COLUMNS = 1;
export const MAX_COMPARISON_ROWS = 16;
export const MIN_COMPARISON_ROWS = 1;

export const DEFAULT_COMPARISON_COLUMN_DEFS: LandingComparisonColumn[] = [
  { id: "ours", label: BRAND_NAME, highlight: true },
  { id: "other", label: "ChatGPT / LLM" },
  { id: "third", label: "Агентство" },
];

export const DEFAULT_COMPARISON_COLUMNS: LandingComparisonColumns = {
  featureLabel: "Критерий",
  oursLabel: BRAND_NAME,
  otherLabel: "ChatGPT / LLM",
  thirdLabel: "Агентство",
};

export const DEFAULT_COMPARISON_ROWS: LandingComparisonRow[] = [
  {
    feature: "Структура отчёта 11+ стр.",
    ours: "yes",
    other: "no",
    third: "yes",
    cells: { ours: "yes", other: "no", third: "yes" },
  },
  {
    feature: "Скоринг по критериям",
    ours: "yes",
    other: "no",
    third: "Частично",
    cells: { ours: "yes", other: "no", third: "Частично" },
  },
  {
    feature: "Источники с ссылками",
    ours: "yes",
    other: "Нестабильно",
    third: "yes",
    cells: { ours: "yes", other: "Нестабильно", third: "yes" },
  },
  {
    feature: "Wordstat (Яндекс)",
    ours: "yes",
    other: "no",
    third: "Опционально",
    cells: { ours: "yes", other: "no", third: "Опционально" },
  },
  {
    feature: "Срок",
    ours: "5–10 мин",
    other: "Мгновенно",
    third: "2–6 недель",
    cells: { ours: "5–10 мин", other: "Мгновенно", third: "2–6 недель" },
  },
  {
    feature: "Стоимость",
    ours: "от 4 990 ₽",
    other: "Подписка LLM",
    third: "от 99 000 ₽",
    cells: { ours: "от 4 990 ₽", other: "Подписка LLM", third: "от 99 000 ₽" },
  },
  {
    feature: "Сравнение гипотез",
    ours: "yes",
    other: "no",
    third: "no",
    cells: { ours: "yes", other: "no", third: "no" },
  },
];

export const DEFAULT_COMPARISON = {
  sectionId: "comparison",
  title: "Не ChatGPT. Не агентство.",
  subtitle: `ChatGPT даёт текст без методологии, агентство — глубину за сотни тысяч и недели ожидания. ${BRAND_NAME} соединяет структуру кабинетного исследования, реальный Wordstat и портфель гипотез.`,
  backgroundImageUrl: "",
  ...DEFAULT_COMPARISON_COLUMNS,
  columns: DEFAULT_COMPARISON_COLUMN_DEFS,
  rows: DEFAULT_COMPARISON_ROWS,
};

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asLabel(value: unknown, fallback: LandingTextValue): LandingTextValue {
  if (value === undefined || value === null) return fallback;
  return asLandingTextValue(value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function newComparisonColumnId(): string {
  return `col_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function emptyRowCells(
  columns: LandingComparisonColumn[],
): Record<string, LandingComparisonCell> {
  return Object.fromEntries(
    columns.map((col) => [
      col.id,
      { mode: "text" as const, text: "", content: "" },
    ]),
  );
}

/** Старый токен {price} больше не подставляет тариф — это обычный текст. */
function migratePriceToken(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "{price}" || trimmed.toLowerCase() === "price") {
    return "от 4 990 ₽";
  }
  return value;
}

export function parseComparisonCell(raw: unknown): LandingComparisonCell {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const rec = raw as Record<string, unknown>;
    if (rec.mode === "check" || rec.mode === "cross" || rec.mode === "text") {
      const text = typeof rec.text === "string" ? rec.text : "";
      const content =
        rec.content !== undefined ? asLandingTextValue(rec.content) : text;
      return {
        mode: rec.mode,
        text: styledTextString(content) || text,
        content,
      };
    }
  }
  if (typeof raw !== "string") {
    return { mode: "text", text: "", content: "" };
  }
  const value = migratePriceToken(raw);
  const lower = value.trim().toLowerCase();
  if (lower === "yes" || lower === "true" || lower === "да") {
    return { mode: "check", text: "" };
  }
  if (lower === "no" || lower === "false" || lower === "нет") {
    return { mode: "cross", text: "" };
  }
  return { mode: "text", text: value, content: value };
}

export function comparisonCellToLegacyString(cell: LandingComparisonCell): string {
  if (cell.mode === "check") return "yes";
  if (cell.mode === "cross") return "no";
  return styledTextString(cell.content ?? cell.text);
}

export function comparisonCellContent(cell: LandingComparisonCell): LandingTextValue {
  if (cell.content !== undefined) return cell.content;
  return cell.text;
}

/** check → галочка, cross → крест, text → значение со стилем. */
export function resolveComparisonCell(
  raw: LandingComparisonCell | string | undefined,
): boolean | LandingTextValue {
  const cell = parseComparisonCell(raw ?? "");
  if (cell.mode === "check") return true;
  if (cell.mode === "cross") return false;
  return comparisonCellContent(cell);
}

function rawCellValue(
  row: Record<string, unknown>,
  columnId: string,
): unknown {
  const cells = asRecord(row.cells);
  if (Object.prototype.hasOwnProperty.call(cells, columnId)) {
    return cells[columnId];
  }

  if (columnId === "ours") {
    if (typeof row.ours === "string") return row.ours;
    if (typeof row.idealitika === "string") return row.idealitika;
  }
  if (columnId === "other") {
    if (typeof row.other === "string") return row.other;
    if (typeof row.chatgpt === "string") return row.chatgpt;
  }
  if (columnId === "third") {
    if (typeof row.third === "string") return row.third;
    if (typeof row.agency === "string") return row.agency;
  }
  return undefined;
}

function normalizeColumnList(input: {
  columns?: unknown;
  oursLabel?: unknown;
  otherLabel?: unknown;
  thirdLabel?: unknown;
}): LandingComparisonColumn[] {
  if (Array.isArray(input.columns) && input.columns.length > 0) {
    const used = new Set<string>();
    const columns = input.columns
      .map((item, index) => {
        const col = asRecord(item);
        let id = asString(col.id).trim() || `col_${index}`;
        while (used.has(id)) id = `${id}_${index}`;
        used.add(id);
        return {
          id,
          label: asLabel(col.label, "Колонка"),
          highlight: col.highlight === true || col.highlight === "true",
        };
      })
      .slice(0, MAX_COMPARISON_COLUMNS);
    if (columns.some((col) => col.highlight)) return columns;
    return columns.map((col, index) =>
      index === 0 ? { ...col, highlight: true } : col,
    );
  }

  return [
    {
      id: "ours",
      label: asLabel(
        input.oursLabel,
        DEFAULT_COMPARISON_COLUMNS.oursLabel,
      ),
      highlight: true,
    },
    {
      id: "other",
      label: asLabel(
        input.otherLabel,
        DEFAULT_COMPARISON_COLUMNS.otherLabel,
      ),
    },
    {
      id: "third",
      label: asLabel(
        input.thirdLabel,
        DEFAULT_COMPARISON_COLUMNS.thirdLabel,
      ),
    },
  ];
}

function attachLegacyCells(
  cells: Record<string, LandingComparisonCell>,
  columns: LandingComparisonColumn[],
): Pick<LandingComparisonRow, "ours" | "other" | "third"> {
  const [first, second, third] = columns;
  return {
    ours: first ? comparisonCellToLegacyString(cells[first.id] ?? { mode: "text", text: "" }) : "",
    other: second
      ? comparisonCellToLegacyString(cells[second.id] ?? { mode: "text", text: "" })
      : "",
    third: third
      ? comparisonCellToLegacyString(cells[third.id] ?? { mode: "text", text: "" })
      : "",
  };
}

export function normalizeComparisonTable(input: {
  featureLabel?: unknown;
  oursLabel?: unknown;
  otherLabel?: unknown;
  thirdLabel?: unknown;
  columns?: unknown;
  rows?: unknown;
}): LandingComparisonTable {
  const columns = normalizeColumnList(input);
  const featureLabel = asLabel(
    input.featureLabel,
    DEFAULT_COMPARISON_COLUMNS.featureLabel,
  );

  if (!Array.isArray(input.rows)) {
    return {
      featureLabel,
      columns,
      rows: DEFAULT_COMPARISON_ROWS.map((row) => {
        const cells = emptyRowCells(columns);
        for (const col of columns) {
          cells[col.id] = parseComparisonCell(rawCellValue(row, col.id));
        }
        return {
          feature: asLabel(row.feature, ""),
          cells,
        };
      }),
    };
  }

  const rows = input.rows.map((item) => {
    const row = asRecord(item);
    const cells = emptyRowCells(columns);
    for (const col of columns) {
      cells[col.id] = parseComparisonCell(rawCellValue(row, col.id));
    }
    return {
      feature: asLabel(row.feature, ""),
      cells,
    };
  });

  return { featureLabel, columns, rows };
}

export function serializeComparisonTable(table: LandingComparisonTable): {
  featureLabel: LandingTextValue;
  oursLabel: string;
  otherLabel: string;
  thirdLabel: string;
  columns: LandingComparisonColumn[];
  rows: LandingComparisonRow[];
} {
  const [first, second, third] = table.columns;
  return {
    featureLabel: table.featureLabel,
    oursLabel: styledTextString(first?.label ?? ""),
    otherLabel: styledTextString(second?.label ?? ""),
    thirdLabel: styledTextString(third?.label ?? ""),
    columns: table.columns,
    rows: table.rows.map((row) => ({
      feature: row.feature,
      cells: row.cells,
      ...attachLegacyCells(row.cells, table.columns),
    })),
  };
}

export function normalizeComparisonRows(
  rows: unknown,
): LandingComparisonNormalizedRow[] {
  return normalizeComparisonTable({ rows }).rows;
}
